import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";
import "../../App.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Button, Box, Typography, Menu, MenuItem } from "@material-ui/core";
import FormDialog from "../../components/createFeeDialog";
import CustomCheckbox from "../../components/checkboxRenderer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LicenseManager } from "ag-grid-enterprise";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RedoIcon from "@material-ui/icons/Redo";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import fnOpenToast from "../../components/toast";
import { SettingsOutlined } from "@material-ui/icons";
import { Link as ReactLink, useNavigate, useParams } from "react-router-dom";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MultiSelectCheckbox from "../../components/multiSelectCheckbox";
import { camelCaseToTitle } from "../../utils/camelCaseToTitle";
import { sortPriorityColumns } from "../../utils/sortPriorityColumns";
import { validateTransportationFeeConfig } from "../../utils/validateTransportationFeeConfig";
import { notifyError, notifySuccess } from "../../utils/toast";
import {
    getFees,
    getEnums,
    getCollections,
    createFee,
    updateFee,
    deleteFees,
    getStates,
    getCountriesCurrencies,
} from "../../api";
import withMetadataGridMultiSelectCheckbox from "../../hocs/withMetadataGridMultiSelectCheckbox";

toast.configure();
LicenseManager.setLicenseKey(
    "Using_this_AG_Grid_Enterprise_key_( AG-044136 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Lucid USA, Inc )_is_granted_a_( Single Application )_Developer_License_for_the_application_( V360 )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( V360 )_need_to_be_licensed___( V360 )_has_been_granted_a_Deployment_License_Add-on_for_( 2 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 28 August 2024 )____[v2]_MTcyNDc5OTYwMDAwMA==c89ee173c77380f5ddc3c6af80f11777",
);

const initialValue = {
    feeCountry: {
        value: "US",
        hasError: false,
    },
    feeState: {
        value: "",
        hasError: false,
    },
    feeCurrency: {
        value: "",
        hasError: false,
    },
    feeAmount: {
        value: "",
        hasError: false,
    },
    feeTaxable: {
        value: true,
        hasError: false,
    },
    feeTaxRate: {
        value: "",
        hasError: false,
    },
    feeActive: {
        value: true,
        hasError: false,
    },
    selfReg: {
        value: false,
        hasError: false,
    },
    description: {
        value: "",
        hasError: false,
    },
    isDeleted: {
        value: false,
        hasError: false,
    },
    transportationFeeConfig: {
        value: undefined,
        hasError: false,
    },
    metadata: {
        value: {},
    },
};

function FeeAdmin() {
    const { country: currentCountry } = useParams();
    const navigate = useNavigate();

    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [open, setOpen] = React.useState(false);
    const [formData, setFormData] = useState(initialValue);
    const [rowsUpdated, setRowsUpdated] = useState(new Map());
    const [enableEditing, setEnableEditing] = useState(false);
    const [constantsData, setConstantsData] = useState(null);
    const [countriesList, setCountriesList] = useState(null);
    const [countries, setCountries] = useState(new Map());
    const [statesList, setStatesList] = useState(null);
    const [countryOption, setCountryOption] = useState([]);
    const [states, setStates] = useState(new Map());
    const [country, setCountry] = useState(currentCountry);
    const [metadataCollections, setMetadataCollections] = useState([]);

    const validConstantsData = useMemo(() => {
        const countryConstants = {};
        if (!constantsData) {
            return countryConstants;
        }
        for (const [collection, data] of Object.entries(constantsData)) {
            countryConstants[collection] = data.filter(
                ({ countries, isActive }) => {
                    if (!countries) {
                        return true;
                    }
                    return countries.includes(country) && isActive;
                },
            );
        }

        return countryConstants;
    }, [constantsData, country]);

    const supportedMetadataCollections = useMemo(
        () =>
            metadataCollections.filter(
                (collection) =>
                    country === "OTHERS" ||
                    collection.countries.some((c) => c === country),
            ),
        [metadataCollections, country],
    );

    const frameworkComponents = {
        checkboxRenderer: CustomCheckbox,
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setInitialData();
    };

    const components = useMemo(
        () => ({
            checkboxRenderer: CustomCheckbox,
        }),
        [],
    );

    //TODO: refactor this to do not depend on this map
    const iso3Codes = {
        US: "USA",
        CA: "CAN",
        OTHERS: "MEX",
    };

    const defaultPriorityColumns = [
        "State",
        "Fee Currency",
        "Name",
        "Type",
        "Category",
        "Subcategory",
        "Fee Amount",
        "Fee Tax Rate",
        "Fee Taxable",
        "Fee Active",
        "Self Reg",
        "Description",
    ];

    const staticColumns = [
        {
            headerName: "Amount",
            field: "feeAmount",
            cellEditor: "agNumberCellEditor",
            cellEditorParams: { min: 0 },
            cellRenderer: (params) => {
                return params?.data?.type === "Credit" ||
                    params?.data?.type === "Rebate"
                    ? params?.data?.feeAmount * -1
                    : params?.data?.feeAmount;
            },
            width: 110,
        },
        {
            headerName: "Currency",
            field: "feeCurrency",
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: (params) => {
                const feeCountry = countries.get(params.data.feeCountry);
                const currencies = new Map();
                feeCountry.currencyLists.map((item) => {
                    currencies.set(item.currencyCode, item);
                });
                return {
                    values: currencies && Array.from(currencies.keys()),
                    formatValue: (value) => {
                        const currency = currencies.get(value);
                        if ((!value && currencies.size === 0) || !currency) {
                            return "";
                        }
                        return `${currency.currencyName} - ${currency.currencyCode}`;
                    },
                };
            },
            width: 110,
        },
        {
            headerName: "State",
            field: "feeState",
            cellEditor: "agRichSelectCellEditor",
            cellEditorPopup: true,
            cellEditorParams: () => {
                return {
                    values: states && Array.from(states.keys()),
                    formatValue: (value) => {
                        const state = states.get(value);
                        if ((!value && states.size === 0) || !state) {
                            return "";
                        }
                        return `${state.stateProvinceName} - ${state.stateProvinceCode}`;
                    },
                };
            },
            width: 110,
        },
        {
            headerName: "Rate",
            field: "feeTaxRate",
            width: 110,
        },
        {
            headerName: "Active",
            field: "feeActive",
            cellRenderer: CustomCheckbox,
            width: 100,
        },
        {
            headerName: "Self Reg",
            field: "selfReg",
            cellRenderer: "checkboxRenderer",
            width: 100,
        },
        {
            headerName: "Taxable",
            field: "feeTaxable",
            cellRenderer: "checkboxRenderer",
            width: 100,
        },
        {
            headerName: "Description",
            field: "description",
            width: 200,
        },
        {
            headerName: "Dealertrack ID",
            field: "dealertrackId",
            cellEditor: "agTextCellEditor",
            hide: country != "CA",
            width: 200,
        },
        {
            headerName: "Transportation Fee Config",
            field: "transportationFeeConfig",
            cellEditor: "agTextCellEditor",
            cellRenderer: (params) => {
                return params.value?.length > 0
                    ? JSON.stringify(params.value)
                    : "";
            },
            valueGetter: (params) => {
                return params.data[params.colDef.field]?.length > 0
                    ? JSON.stringify(params.data[params.colDef.field])
                    : "";
            },
            filter: false,
            onCellValueChanged: (params) => {
                if (!validateTransportationFeeConfig(params.newValue)) {
                    alert("The entered JSON is not valid.");
                    params.node.setDataValue(
                        params.column,
                        JSON.parse(params.oldValue),
                        { suppressChangeDetection: true },
                    );
                } else {
                    handleCellValueChanged(params);
                }
            },
            valueFormatter: (params) => {
                return params.value?.length
                    ? JSON.stringify(params.value, null, 2)
                    : "";
            },
            width: 200,
            editable: (params) =>
                enableEditing && params.data.name === "Transportation Fee",
            onCellDoubleClicked: (event) => {
                if (event.data.name !== "Transportation Fee") {
                    alert(
                        'The fee name is not "Transportation Fee". You cannot edit this cell.',
                    );
                    return;
                }
            },
            valueParser: (params) => {
                try {
                    return JSON.parse(params.newValue);
                } catch (e) {
                    alert("The entered JSON is not valid.");
                    return params.oldValue;
                }
            },
        },
    ];

    const columnDefs = useMemo(
        () =>
            sortPriorityColumns(defaultPriorityColumns, [
                ...supportedMetadataCollections.map((collection) => {
                    return collection.isMultipleSelection
                        ? {
                              headerName: camelCaseToTitle(collection.name),
                              field: collection?.metadata?.[collection.name],
                              cellEditor:
                                  withMetadataGridMultiSelectCheckbox(
                                      MultiSelectCheckbox,
                                  ),
                              cellEditorParams: (params) => {
                                  return {
                                      values:
                                          validConstantsData[collection.name] ??
                                          [],
                                      eGridCell: params.eGridCell,
                                  };
                              },
                              width: 200,
                              cellRenderer: ({ value }) => {
                                  if (!constantsData[collection.name]) {
                                      return null;
                                  }

                                  if (
                                      value?.appliesToAll &&
                                      (value?.elements?.length === 0 ||
                                          value?.elements?.length ===
                                              constantsData[collection.name]
                                                  ?.length)
                                  ) {
                                      return "All";
                                  }

                                  return constantsData[collection.name]
                                      .filter((item) =>
                                          value?.elements
                                              ?.map((el) => el?._id)
                                              ?.includes(item?._id),
                                      )
                                      .map((item) => item?.name)
                                      .join(", ");
                              },
                              valueGetter: ({ data }) => {
                                  return data?.metadata?.[collection?.name];
                              },
                              valueSetter: ({ data, newValue }) => {
                                  data.metadata[collection?.name] = newValue;
                                  return true;
                              },
                              filter: "agSetColumnFilter",
                              filterParams: {
                                  getValue: (params) => {
                                      if (
                                          params.data.metadata[collection.name]
                                              ?.appliesToAll ||
                                          params.data.metadata[collection.name]
                                              ?.elements?.length === 0
                                      ) {
                                          return constantsData[
                                              collection.name
                                          ]?.map((el) => el._id);
                                      }
                                      return params.data.metadata[
                                          collection.name
                                      ]?.elements.map((el) => el._id);
                                  },
                                  valueFormatter: (params) => {
                                      const ids =
                                          params.value?.split(", ") || [];
                                      return constantsData[collection.name]
                                          .filter((option) =>
                                              ids.includes(option._id),
                                          )
                                          .map((option) => option.name)
                                          .join(", ");
                                  },
                              },
                          }
                        : {
                              headerName: camelCaseToTitle(collection?.name),
                              field: collection?.metadata?.[collection.name]
                                  ?.elements[0],
                              width: 200,
                              cellEditor: "agRichSelectCellEditor",
                              cellEditorParams: {
                                  values:
                                      validConstantsData[collection?.name] ??
                                      [],
                                  formatValue: (value) => value?.name,
                              },
                              cellRenderer: ({ value }) => {
                                  return value?.name || null;
                              },
                              valueGetter: (value) => {
                                  return value?.data?.metadata?.[
                                      collection?.name
                                  ]?.elements[0];
                              },
                              valueSetter: ({ data, newValue }) => {
                                  if (!data.metadata[collection?.name]) {
                                      data.metadata[collection?.name] = {
                                          elements: [],
                                          appliesToAll: false,
                                      };
                                  }
                                  data.metadata[collection?.name].elements = [
                                      newValue,
                                  ];
                              },
                              filter: "agSetColumnFilter",
                              filterParams: {
                                  getValue: (params) => {
                                      if (
                                          params.data.metadata[collection.name]
                                              ?.appliesToAll ||
                                          params.data.metadata[collection.name]
                                              ?.elements?.length === 0
                                      ) {
                                          return constantsData[
                                              collection.name
                                          ]?.map((el) => el._id);
                                      }
                                      return params.data.metadata[
                                          collection.name
                                      ]?.elements.map((el) => el._id);
                                  },
                                  valueFormatter: (params) => {
                                      const ids =
                                          params.value?.split(", ") || [];
                                      return (
                                          constantsData[collection.name]
                                              .filter((option) =>
                                                  ids.includes(option._id),
                                              )
                                              .map((option) => option.name)
                                              .join(", ") || "Not Assigned"
                                      );
                                  },
                              },
                          };
                }),
                ...staticColumns,
            ]),
        [supportedMetadataCollections],
    );

    useEffect(() => {
        const selectedCountry = countries.get(country);
        if (selectedCountry) {
            refreshStates(selectedCountry.countryISO3Code);
        }
    }, [country]);

    useEffect(() => {
        refreshData();
        setFormData({
            ...formData,
            feeCountry: { value: country, hasError: false },
        });
        localStorage.setItem("country", country);
        refreshStates(iso3Codes[country]);
    }, [country]);

    useEffect(() => {
        if (gridApi && tableData) {
            // https://lucidmotors.atlassian.net/browse/RTLCOMMSYS-17200
            // restrictedColumns.forEach(column => gridApi.columnApi.setColumnVisible(column, tableData.some(fee => fee[column] !== undefined)));
        }
    }, [gridApi, tableData]);

    const handleSetCountry = (event) => {
        const selectedCountry =
            event.target.value === "OTHERS" ? "MX" : event.target.value;
        setCountry(selectedCountry);
        navigate(`/vehiclesalesfees/${selectedCountry}`);
    };

    const refreshData = async (state) => {
        const request = {
            feeCountry: country,
            feeState: state || "ALL",
            includeInactiveFee: true,
            includeMetadata: true,
        };
        const fees = await getFees(request);
        setTableData(
            fees?.map((fee) => ({
                ...fee,
                feeAmount: Math.abs(fee.feeAmount),
            })),
        );
        const enums = await getEnums();
        setConstantsData(enums);
    };

    const refreshStates = async (countryISO3Code) => {
        const states = await getStates(countryISO3Code);
        setStatesList(states);

        if (states.length > 0) {
            const statesMap = new Map();
            states.map((item) => {
                statesMap.set(item.stateProvinceCode, item);
            });
            setStates(statesMap);
            columnDefs.find(
                (col) => col.field === "feeState",
            ).cellEditorParams.values =
                statesMap && Array.from(statesMap.keys());
            gridRef.current.api.setColumnDefs(columnDefs);
            return;
        }

        setStates(new Map());
        columnDefs.find(
            (col) => col.field === "feeState",
        ).cellEditorParams.values = [];
        if (gridRef?.curr?.api) {
            gridRef.current.api.setColumnDefs(columnDefs);
        }
    };

    const getInitialData = async () => {
        getCountriesList();
        refreshStates(iso3Codes[country]);

        const enums = await getEnums();
        setConstantsData(enums);

        const collections = await getCollections();
        setMetadataCollections(collections);
        setInitialData(collections);
    };

    const setInitialData = (collections = metadataCollections) => {
        const metadata = collections.reduce(
            (acc, curr) => ({
                ...acc,
                [curr.name]: curr.isMultipleSelection
                    ? {
                          elements: [],
                          appliesToAll: true,
                      }
                    : {
                          elements: [],
                          appliesToAll: false,
                      },
            }),
            {},
        );
        setFormData((currData) => ({ ...currData, metadata }));
    };

    const getCountriesList = async () => {
        const countriesCurrencies = await getCountriesCurrencies();
        let countryOptionArr = [];
        countriesCurrencies.map((item) => {
            countries.set(item.countryISO2Code, item);
            const countryOption = {
                label: `${item.countryISO2Code} - ${item.countryName}`,
                value: item.countryISO2Code,
            };
            countryOptionArr.push(countryOption);
        });
        setCountries(countries);
        setCountryOption(countryOptionArr);
        setCountriesList(countriesCurrencies);
    };

    const onChange = (e) => {
        const { value, id, name } = e.target;
        if (id) {
            setFormData({
                ...formData,
                [id]: { value: value, hasError: false },
            });
            return;
        }

        if (name.startsWith("metadata")) {
            const fieldName = name.split(".")[1];
            if (!Array.isArray(value)) {
                setFormData({
                    ...formData,
                    metadata: {
                        ...formData.metadata,
                        [fieldName]: { value, hasError: false },
                    },
                });
                return;
            }
            const isAllFlag = value?.some(({ appliesToAll }) => appliesToAll);
            const selectedValues = isAllFlag
                ? [{ appliesToAll: true }]
                : value.filter((v) => !v.appliesToAll);

            setFormData({
                ...formData,
                metadata: {
                    ...formData.metadata,
                    [fieldName]: { value: selectedValues, hasError: false },
                },
            });
            return;
        }
        setFormData({ ...formData, [name]: { value: value, hasError: false } });
    };

    const onStateChanged = (e) => {
        if (e.target.value.stateProvinceCode) {
            setFormData({
                ...formData,
                [e.target.name]: {
                    value: e.target.value.stateProvinceCode,
                    hasError: false,
                },
            });
        } else if (e.target.value === "ALL") {
            setFormData({
                ...formData,
                [e.target.name]: { value: e.target.value, hasError: false },
            });
        } else {
            setFormData({
                ...formData,
                [e.target.name]: { value: "", hasError: true },
            });
        }
    };

    const onChecked = (e) => {
        const { checked, id } = e.target;
        setFormData({ ...formData, [id]: { value: checked, hasError: false } });
    };

    useEffect(() => {
        getInitialData();
    }, []);

    const onGridReady = (params) => {
        setGridApi(params);
    };

    const onCellEditingStopped = (event) => {
        if (event.value === null) {
            event.node.setDataValue(event.column.colId, event.oldValue);
        }
    };

    const handleCellValueChanged = (event) => {
        if (!event?.data?.feeCurrency) {
            fnOpenToast("Currency field is required", "error");
            return;
        } else if (!event?.data?.feeState) {
            fnOpenToast("State field is required", "error");
            return;
        } else {
            setRowsUpdated(rowsUpdated.set(event?.data?._id, event?.data));
        }
    };

    const handleEnableEdit = () => {
        setEnableEditing(true);
    };

    const handleCancelEdit = () => {
        setEnableEditing(false);
        setRowsUpdated(new Map());
        refreshData();
    };

    const handleSaveEdited = () => {
        const editedRows = Array.from(rowsUpdated.values());

        const transformedRows = editedRows.map((row) => ({
            ...row,
            feeAmount:
                row?.type === "Credit" || row?.type === "Rebate"
                    ? row.feeAmount * -1
                    : row.feeAmount,
            metadata: (() => {
                const metadata = {};
                for (const [collection, value] of Object.entries(
                    row.metadata,
                )) {
                    metadata[collection] = {
                        appliesToAll:
                            value.appliesToAll ||
                            value.elements.length ===
                                validConstantsData[collection]?.length,
                        elements: value.elements.map((el) => el._id),
                    };
                }
                return metadata;
            })(),
        }));
        handleUpdateFee(transformedRows);
        setRowsUpdated(new Map());
    };

    const handleUpdateFee = async (editedRows) => {
        const updateRequest = {
            updateFees: editedRows,
        };
        const confirm = window.confirm(
            "Are you sure, you want to update this row ?",
        );
        if (!confirm) {
            return;
        }

        setEnableEditing(false);
        try {
            const errorOcurred = await updateFee(updateRequest);
            !errorOcurred
                ? fnOpenToast("Fees Updated Successfully!", "success")
                : notifyError(errorOcurred?.message || "Something went wrong");
        } catch (error) {
            notifyError(error.message);
        }

        refreshData();
    };

    const handleFormSubmit = async (event) => {
        try {
            event.preventDefault();
            const request = constructRequest(formData);
            await createFee(request);

            handleClose();
            refreshData();
            notifySuccess();
        } catch (error) {
            notifyError(error.message);
        }
    };

    const constructMetadata = (dataSource) => {
        const metadata = {};
        for (const [collection, { value }] of Object.entries(dataSource)) {
            if (!value) {
                continue;
            }
            const { isMultipleSelection } = metadataCollections.find(
                ({ name }) => name === collection,
            );
            if (isMultipleSelection) {
                const appliesToAll = value.some((val) => val?.appliesToAll);
                const elements = appliesToAll
                    ? []
                    : value
                          .map((el) => el._id)
                          .filter((val) => !val?.appliesToAll);
                metadata[collection] = {
                    appliesToAll,
                    elements,
                };
                continue;
            }
            metadata[collection] = {
                elements: [value._id],
                appliesToAll: false,
            };
        }
        return metadata;
    };

    const constructRequest = (formData) => {
        const metadata = constructMetadata(formData.metadata);
        const request = {
            feeCountry: country,
            feeState: formData.feeState.value,
            feeAmount:
                (formData.metadata.type.value.name === "Credit" ||
                formData.metadata.type.value.name === "Rebate"
                    ? "-" + formData.feeAmount.value
                    : formData.feeAmount.value) || 0,
            feeTaxable: formData.feeTaxable.value,
            feeTaxRate:
                formData.feeTaxRate.value === ""
                    ? null
                    : formData.feeTaxRate.value,
            feeActive: formData.feeActive.value,
            selfReg: formData.selfReg.value,
            description: formData.description.value,
            isDeleted: formData.isDeleted.value,
            transportationFeeConfig:
                (!!formData?.transportationFeeConfig?.value &&
                    JSON.parse(formData.transportationFeeConfig.value)) ||
                [],
            metadata,
        };

        return request;
    };

    const handleDeleteFee = async () => {
        const selectedRows = gridRef.current.api.getSelectedRows();
        if (selectedRows.length === 0) {
            fnOpenToast("Select records to delete !!!", "error");
            return;
        }

        const confirm = window.confirm(
            "Are you sure, you want to delete these rows?",
        );
        if (!confirm) {
            return;
        }

        const deleteRequest = {
            updateFees: selectedRows,
        };
        try {
            const errorOcurred = await deleteFees(deleteRequest);
            !errorOcurred
                ? fnOpenToast("Fees Deleted Successfully!", "success")
                : notifyError(errorOcurred?.message || "Something went wrong");
        } catch (error) {
            notifyError(error.message);
        }

        await refreshData();
    };

    const defaultColDef = {
        sortable: true,
        filter: true,
        floatingFilter: true,
        editable: enableEditing,
        resizable: true,
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleActionClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);
    const handleActionClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    return (
        <div>
            <div style={{ margin: "10px 20px" }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <ReactLink
                        color="inherit"
                        to="/"
                        className="breadcrumb-link"
                    >
                        Dashboard
                    </ReactLink>
                    <Typography color="textPrimary">
                        Fee Administration
                    </Typography>
                </Breadcrumbs>
            </div>
            <Box
                display="flex"
                justifyContent="space-between"
                style={{ margin: "10px 20px" }}
            >
                <div>
                    <Typography
                        variant="h5"
                        component={"h5"}
                        style={{
                            fontFamily: "GTAmericaMedium",
                            fontSize: "24px",
                            lineHeight: "120%",
                            fontWeight: "500",
                        }}
                    >
                        Fee Administration
                    </Typography>
                </div>
                <>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {country === "US" && (
                            <div style={{ marginRight: "10px" }}>
                                <ReactLink
                                    to="/special-plate-fees/CA"
                                    style={{ marginLeft: "5px", color: "#000" }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        aria-controls="action-menu"
                                        aria-haspopup="true"
                                        onClick={handleActionClick}
                                    >
                                        {" "}
                                        Special Interest Plate Fees{" "}
                                    </Button>
                                </ReactLink>
                            </div>
                        )}

                        <div style={{ marginRight: "10px" }}>
                            <FormControl variant="outlined">
                                <InputLabel id="demo-simple-select-autowidth-label">
                                    Country
                                </InputLabel>
                                <Select
                                    labelId="demo-simple-select-outlined-label"
                                    id="demo-simple-select-outlined"
                                    value={country}
                                    onChange={handleSetCountry}
                                    label="Country"
                                >
                                    <MenuItem value={"US"}>US</MenuItem>
                                    <MenuItem value={"CA"}>CA</MenuItem>
                                    <MenuItem value={"OTHERS"}>Others</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                aria-controls="action-menu"
                                aria-haspopup="true"
                                onClick={handleActionClick}
                            >
                                Actions <ExpandMoreIcon />
                            </Button>
                            <Menu
                                id="action-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleActionClose}
                            >
                                <MenuItem onClick={handleEnableEdit}>
                                    <EditIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Enable Editing
                                </MenuItem>
                                <MenuItem
                                    onClick={handleSaveEdited}
                                    disabled={!enableEditing}
                                >
                                    <CheckCircleOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Save Changes
                                </MenuItem>
                                <MenuItem
                                    onClick={handleCancelEdit}
                                    disabled={!enableEditing}
                                >
                                    <RedoIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Cancel Changes
                                </MenuItem>
                                <MenuItem
                                    onClick={handleClickOpen}
                                    disabled={enableEditing}
                                >
                                    <AddCircleOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Add Fee
                                </MenuItem>
                                <MenuItem
                                    onClick={handleDeleteFee}
                                    disabled={enableEditing}
                                >
                                    <DeleteOutlineIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Delete Selected Fee(s)
                                </MenuItem>
                            </Menu>
                            <ReactLink
                                to={`/metadata/${country}/name`}
                                style={{
                                    marginLeft: "5px",
                                    color: "#000",
                                    textAlign: "center",
                                }}
                            >
                                <SettingsOutlined />
                            </ReactLink>
                        </div>
                    </Box>
                </>
            </Box>
            <table
                style={{
                    margin: "0 20px",
                    width: "calc(100% - 40px)",
                    height: "calc(100vh - 170px)",
                }}
            >
                <tbody>
                    <tr>
                        <td style={{ border: "1px solid #babfc7" }}>
                            <div
                                className="ag-theme-alpine"
                                style={{
                                    height: "100%",
                                    width: "100%",
                                }}
                            >
                                <AgGridReact
                                    rowData={tableData}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    onGridReady={onGridReady}
                                    ref={gridRef}
                                    rowSelection="multiple"
                                    onCellValueChanged={handleCellValueChanged}
                                    components={components}
                                    onCellEditingStopped={onCellEditingStopped}
                                />
                            </div>
                            {constantsData && (
                                <FormDialog
                                    open={open}
                                    handleClose={handleClose}
                                    data={formData}
                                    onChange={onChange}
                                    onChecked={onChecked}
                                    onStateChanged={onStateChanged}
                                    handleFormSubmit={handleFormSubmit}
                                    constantsData={validConstantsData}
                                    statesList={statesList}
                                    validateTransportationFeeConfig={
                                        validateTransportationFeeConfig
                                    }
                                    metadataCollections={
                                        supportedMetadataCollections
                                    }
                                />
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export default FeeAdmin;
